/**
 * Storage client — replaces `supabase.storage.from(bucket)`.
 *
 * Only the three operations the app uses are implemented: upload, public URL and
 * remove. Anything else throws rather than returning something plausible.
 *
 * THE PATH IS NOT THE CLIENT'S CHOICE
 * -----------------------------------
 * Supabase had the caller build `{userId}/{filename}` and the server trusted it.
 * Here the server generates the object key from the verified token and returns it,
 * so `upload()` takes the File, not a path, and callers use the returned `url`.
 *
 * `getPublicUrl(path)` still works for objects the server named (that is how an
 * existing `profiles.avatar_url` is re-rendered), and `pathFromUrl()` recovers the
 * key from a stored URL so a delete can target the right object.
 */

const TIMEOUT_MS = 60_000; // uploads of a few MB on a slow link

function apiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return value ? value.replace(/\/+$/, "") : null;
}

function accessToken(): string | null {
  try {
    const raw = localStorage.getItem("lyder-talk-auth:v1");
    if (!raw) return null;
    return (JSON.parse(raw) as { access_token?: string }).access_token ?? null;
  } catch {
    return null;
  }
}

export interface StorageError {
  message: string;
  status?: number;
}

export interface UploadResult {
  path: string;
  url: string;
}

class StorageBucket {
  constructor(private readonly bucket: string) {}

  /**
   * Upload a file. The server decides the key and returns it with a ready URL.
   *
   * `contentType` defaults to the File's own type; pass it explicitly when the
   * blob has none (e.g. a canvas export).
   */
  async upload(
    file: File | Blob,
    options?: { contentType?: string; upsert?: boolean; scopeId?: string; path?: string },
  ): Promise<{ data: UploadResult | null; error: StorageError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "VITE_API_BASE_URL is not set" } };

    const token = accessToken();
    if (!token) return { data: null, error: { message: "Not signed in" } };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const params = new URLSearchParams();
      if (options?.scopeId) params.set("scopeId", options.scopeId);
      // Passing a path preserves overwrite-by-key semantics (admin recordings);
      // the server still requires it to be inside the caller's namespace.
      if (options?.path) params.set("path", options.path);
      const scope = params.toString() ? `?${params.toString()}` : "";
      // `:bucket/objects` is the route the API actually declares. This used to
      // post to `:bucket/upload`, which no controller mapped, so every avatar and
      // group-avatar upload answered 404 while the preview stayed on screen.
      const response = await fetch(`${base}/v1/storage/${this.bucket}/objects${scope}`, {
        method: "POST",
        headers: {
          "Content-Type": options?.contentType ?? file.type ?? "application/octet-stream",
          Authorization: `Bearer ${token}`,
        },
        body: file,
        signal: controller.signal,
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          data: null,
          error: { message: payload?.message ?? `${response.status} ${response.statusText}`, status: response.status },
        };
      }

      return { data: payload as UploadResult, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message:
            error instanceof DOMException && error.name === "AbortError"
              ? "Upload timed out"
              : error instanceof Error
                ? error.message
                : "Upload failed",
        },
      };
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Public URL for a stored object. Synchronous, because the SPA renders with it
   * (supabase's `getPublicUrl` returned a value, not a promise).
   */
  getPublicUrl(path: string): { data: { publicUrl: string } } {
    const base = apiBaseUrl() ?? "";
    return {
      data: { publicUrl: `${base}/v1/storage/public/${this.bucket}?path=${encodeURIComponent(path)}` },
    };
  }

  /**
   * Download a private object the caller owns (e.g. a practice recording).
   *
   * Authenticated rather than a public URL: a recording is someone's voice, and a
   * guessable link should not be enough to hear it.
   */
  async download(path: string): Promise<{ data: Blob | null; error: StorageError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "VITE_API_BASE_URL is not set" } };

    const token = accessToken();
    if (!token) return { data: null, error: { message: "Not signed in" } };

    try {
      const response = await fetch(
        `${base}/v1/storage/object?bucket=${encodeURIComponent(this.bucket)}&path=${encodeURIComponent(path)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        return { data: null, error: { message: `Could not download the file (${response.status})`, status: response.status } };
      }
      return { data: await response.blob(), error: null };
    } catch (error) {
      return { data: null, error: { message: error instanceof Error ? error.message : "Download failed" } };
    }
  }

  async remove(
    paths: string[],
    options?: { scopeId?: string },
  ): Promise<{ data: { removed: number } | null; error: StorageError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "VITE_API_BASE_URL is not set" } };

    const token = accessToken();
    if (!token) return { data: null, error: { message: "Not signed in" } };

    try {
      const response = await fetch(`${base}/v1/storage/${this.bucket}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paths, scopeId: options?.scopeId }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          data: null,
          error: { message: payload?.message ?? "Could not remove the file", status: response.status },
        };
      }

      return { data: payload as { removed: number }, error: null };
    } catch (error) {
      return { data: null, error: { message: error instanceof Error ? error.message : "Remove failed" } };
    }
  }
}

/** Recover the object key from a URL produced by `getPublicUrl`. */
export function pathFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url, "http://localhost");
    const path = parsed.searchParams.get("path");
    return path ? decodeURIComponent(path) : null;
  } catch {
    return null;
  }
}

export const storage = {
  from(bucket: string): StorageBucket {
    return new StorageBucket(bucket);
  },
};

export type { StorageBucket };
